import React, { useMemo } from 'react';
import { ClientData } from '../types/ClientData';
import { formatCurrency } from '../utils/format';
import { useCommaInput } from '../utils/useCommaInput';
import { fvContributions, fvLump } from '../utils/planCalculations';

interface PlanISAProps {
  data: ClientData;
  onUpdate: (updates: Partial<ClientData>) => void;
}

export default function PlanISA({ data, onUpdate }: PlanISAProps) {
  // ── Core data ─────────────────────────────────────────────────────────────
  const clientAge = data.clientAge || 0;
  const freedomAge = data.targetFreedomAge || 55;
  const yearsToFreedom = Math.max(0, freedomAge - clientAge);
  const isaBalance = data.investmentsBalance || 0;
  const currentContribution = data.isaContribution || 0;
  const isaGrowthRate = data.assumptionIsaGrowth || 0.08;
  const growthPct = isaGrowthRate * 100;

  // ── Live amount — defaults to 0, not current allocation (Brief 46A S9) ───
  const amount = data.planIsaMonthlyContribution ?? 0;

  // ── Handler ───────────────────────────────────────────────────────────────
  const handleAmountChange = (v: number) => {
    onUpdate({ planIsaMonthlyContribution: v });
  };

  const inputProps = useCommaInput(amount, handleAmountChange, true);

  // ── Projection ────────────────────────────────────────────────────────────
  const isaFutureValue = useMemo(() => {
    const lump = fvLump(isaBalance, growthPct, yearsToFreedom);
    const contributions = fvContributions(amount, growthPct, yearsToFreedom);
    return lump + contributions;
  }, [isaBalance, growthPct, amount, yearsToFreedom]);

  // ── Annual allowance check ────────────────────────────────────────────────
  const annualContribution = amount * 12;
  const annualAllowance = 20000;
  const exceedsAllowance = annualContribution > annualAllowance;

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white p-8">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <h1 className="text-4xl font-bold text-ma-navy mb-2">ISA</h1>
        <p className="text-gray-600 mb-6">Your flexible, tax-free savings vehicle.</p>

        {/* Context Banner */}
        <div style={{ backgroundColor: 'rgba(100,218,210,0.1)', borderLeft: '3px solid #64DAD2', padding: '12px 16px', borderRadius: 4, fontSize: 15, fontStyle: 'italic', color: '#0B1A3E', marginBottom: 24 }}>
          Tax-free growth. No restrictions on withdrawals. Your flexible bridge to freedom — especially useful if you want income before pension age.
        </div>

        {/* Current Position Panel */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-8 border-l-4 border-blue-500">
          <h2 className="text-xl font-bold text-ma-navy mb-4">Current Position</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="bg-gray-50 p-4 rounded">
              <p className="text-sm text-gray-600 font-medium">ISA balance</p>
              <p className="text-2xl font-bold text-ma-navy">{formatCurrency(isaBalance)}</p>
            </div>
            <div className="bg-gray-50 p-4 rounded">
              <p className="text-sm text-gray-600 font-medium">Current contribution</p>
              <p className="text-2xl font-bold text-ma-navy">
                {currentContribution > 0 ? `${formatCurrency(currentContribution)}/mo` : 'None set'}
              </p>
            </div>
          </div>
        </div>

        {/* Input Section */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-8 border-t-4 border-blue-500">
          <div className="mb-6">
            <label className="block text-sm font-semibold text-gray-900 mb-2">
              Monthly ISA contribution
            </label>
            {/* Brief 47 S3 — show current allocation context */}
            <p className="text-xs text-gray-500 mb-2">
              Current ISA contribution from allocation: {formatCurrency(currentContribution || 0)}/month
            </p>
            <div className="flex items-center gap-2">
              <span className="text-lg font-semibold text-gray-900">£</span>
              <input
                {...inputProps}
                placeholder="0"
                className="w-32 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              <span className="text-gray-600">/month</span>
            </div>
            <p className="text-xs text-gray-500 mt-2">
              Remaining annual allowance: {formatCurrency(Math.max(0, annualAllowance - annualContribution))}
            </p>
          </div>
        </div>

        {/* Annual allowance warning */}
        {exceedsAllowance && amount > 0 && (
          <div className="mb-8 p-4 bg-amber-50 border-l-4 border-amber-400 rounded-lg text-sm text-amber-900">
            <p className="font-semibold">⚠ ISA allowance is £20,000/year. Reduce contribution or split across tax years.</p>
          </div>
        )}

        {/* 46B S8c — Rolling income allocation */}
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-5 mb-8">
          <h4 className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-3">Your Allocation So Far</h4>
          <div className="space-y-2 text-sm">
            {(data.planDividendReduction || 0) > 0 && (
              <div className="flex justify-between">
                <span className="text-gray-600">Pension commitment</span>
                <span className="font-medium">{formatCurrency(data.planDividendReduction)}/mo</span>
              </div>
            )}
            {amount > 0 && (
              <div className="flex justify-between">
                <span className="text-gray-600">ISA contribution</span>
                <span className="font-medium">{formatCurrency(amount)}/mo</span>
              </div>
            )}
            {(data.planDebtExtraMonthly || 0) > 0 && (
              <div className="flex justify-between">
                <span className="text-gray-600">Debt extra payment</span>
                <span className="font-medium">{formatCurrency(data.planDebtExtraMonthly)}/mo</span>
              </div>
            )}
            {(data.efTopUp || 0) > 0 && (
              <div className="flex justify-between">
                <span className="text-gray-600">Savings top-up</span>
                <span className="font-medium">{formatCurrency(data.efTopUp)}/mo</span>
              </div>
            )}
            {(data.planMortgageOverpayment || 0) > 0 && (
              <div className="flex justify-between">
                <span className="text-gray-600">Mortgage overpayment</span>
                <span className="font-medium">{formatCurrency(data.planMortgageOverpayment)}/mo</span>
              </div>
            )}
            <div className="border-t border-gray-300 pt-2 flex justify-between font-semibold">
              <span className="text-gray-700">Total committed</span>
              <span className="text-ma-navy">
                {formatCurrency(
                  (data.planDividendReduction || 0) +
                  amount +
                  (data.planDebtExtraMonthly || 0) +
                  (data.efTopUp || 0) +
                  (data.planMortgageOverpayment || 0)
                )}/mo
              </span>
            </div>
          </div>
        </div>

        {/* Impact Panel */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-8">
          <h2 className="text-xl font-bold text-ma-navy mb-4">Impact</h2>
          <div className="space-y-4">
            <div className="flex justify-between items-center border-b border-gray-200 pb-3">
              <span className="text-gray-700">ISA balance at freedom age</span>
              <span className="text-2xl font-bold text-ma-navy">{formatCurrency(isaFutureValue)}</span>
            </div>
            <div className="flex justify-between items-center border-b border-gray-200 pb-3">
              <span className="text-gray-700">Monthly income at 4% drawdown</span>
              <span className="font-bold text-lg text-blue-600">
                {formatCurrency((isaFutureValue * 0.04) / 12)}/month
              </span>
            </div>
            <div className="bg-blue-50 p-3 rounded flex items-start gap-2">
              <span className="text-sm font-semibold text-blue-900">
                ✓ Accessible before pension age — flexibility before age 57
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
